import * as React from 'react';
import { Field } from 'react-final-form';
import { Form, Grid, List, Message } from 'semantic-ui-react';

// Global Components
import Condition from 'components/Condition';
// @ts-ignore
import WorkTypeOptions from 'components/WorkTypeOptions';
import FieldValue from 'components/FieldValue';
import FieldValues from 'components/FieldValues';
// @ts-ignore
import WorkTypeIsPieceRateGate from 'scenes/Time/components/WorkTypeIsPieceRateGate';
import LunchBreakOptions from 'scenes/Time/components/LunchBreakOptions';
import PaidBreakOptions from 'scenes/Time/components/PaidBreakOptions';

// Global Form
import RenderCheckbox from 'forms/RenderCheckbox';
import RenderDropdown from 'forms/RenderDropdown';
import RenderField from 'forms/RenderField';
import RenderValue from 'forms/RenderValue';
import RenderValueDollars from 'forms/RenderValueDollars';
import RenderValueHours from 'forms/RenderValueHours';
import validators from 'forms/validators';
import normalizers from 'forms/normalizers';

// Local Components
import SubmittingSection from 'components/SubmissionSection';
import MyTimesheetEntryAlreadyExists from './components/MyTimesheetEntryAlreadyExists';
import TimeRangeFields from './components/TimeRangeFields';
import WorkTypeEntityTypeEntityDropdownField from './components/WorkTypeEntityTypeEntityDropdownField';

// Local Constants
import { QUERY_STRING_PARAM } from '../../../../constants';
import MyTimesheetGroupDropdownField from './components/MyTimesheetGroupDropdownField';

// Validators
const {
    composeValidators,
    isPositiveNumber,
    maxValue1000,
    maxValue10000,
    isRequired
} = validators;

// Normalizers
const { toNumberOrNull, toNull } = normalizers;

interface Props {
    handleSubmit: any;
    personId: number;
}

const MyTimesheetEntryForm = ({ handleSubmit, personId }: Props): any => (
    <Form autoComplete="off" onSubmit={handleSubmit}>
        <Grid className="column-padding">
            <Grid.Row>
                <FieldValues fieldNames={['endTime', 'startTime']}>
                    {TimeRangeFields}
                </FieldValues>
                <Grid.Column width={16}>
                    <FieldValue name="hasEditedUnpaidBreak">
                        {(
                            hasEditedUnpaidBreak: boolean | undefined
                        ): React.ReactElement => (
                            <LunchBreakOptions>
                                {(options: any): React.ReactElement => (
                                    <Field
                                        options={options}
                                        component={RenderDropdown as any}
                                        subscription={{
                                            value: true,
                                            touched: true,
                                            error: true
                                        }}
                                        name="unpaidBreakMinutes"
                                        label="Total Meal Breaks"
                                        placeholder="Enter total meal breaks"
                                        parse={toNull}
                                        disabled={!hasEditedUnpaidBreak}
                                        clearable={hasEditedUnpaidBreak}
                                        fluid
                                        search
                                        selection
                                        selectOnBlur={false}
                                    />
                                )}
                            </LunchBreakOptions>
                        )}
                    </FieldValue>
                </Grid.Column>
                <Grid.Column mobile={16} tablet={16} computer={16}>
                    <Field
                        name="hasEditedUnpaidBreak"
                        component={RenderCheckbox as any}
                        label="I want to manually enter my meal breaks"
                    />
                </Grid.Column>
                <Grid.Column width={16}>
                    <MyTimesheetGroupDropdownField personId={personId} />
                </Grid.Column>
                <Grid.Column width={16}>
                    <WorkTypeOptions>
                        <Field
                            name="workTypeId"
                            label="Task"
                            placeholder="Add task"
                            parse={toNull}
                            fluid
                            component={RenderDropdown as any}
                            subscription={{
                                value: true,
                                touched: true,
                                error: true
                            }}
                            search
                            selection
                            closeOnChange
                            upward={false}
                            clearable
                            selectOnBlur={false}
                            validate={isRequired}
                        />
                    </WorkTypeOptions>
                </Grid.Column>
                <FieldValue name="workTypeId">
                    {(workTypeId: number | undefined): React.ReactElement => (
                        <React.Fragment>
                            <Grid.Column width={16}>
                                <WorkTypeEntityTypeEntityDropdownField
                                    name="entityId"
                                    workTypeId={workTypeId}
                                    validate={isRequired}
                                />
                            </Grid.Column>
                            <WorkTypeIsPieceRateGate workTypeId={workTypeId}>
                                <Grid.Column width={8}>
                                    <Field
                                        name="units"
                                        component={RenderField as any}
                                        subscription={{
                                            value: true,
                                            touched: true,
                                            error: true
                                        }}
                                        label="Count"
                                        type="number"
                                        placeholder="Enter units"
                                        parse={toNumberOrNull}
                                        validate={composeValidators(
                                            isRequired,
                                            isPositiveNumber,
                                            maxValue1000
                                        )}
                                    />
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Field
                                        name="rate"
                                        component={RenderField as any}
                                        subscription={{
                                            value: true,
                                            touched: true,
                                            error: true
                                        }}
                                        label="Rate"
                                        type="number"
                                        placeholder="Enter rate"
                                        parse={toNumberOrNull}
                                        validate={composeValidators(
                                            isRequired,
                                            isPositiveNumber,
                                            maxValue10000
                                        )}
                                    />
                                </Grid.Column>
                                <Grid.Column width={16}>
                                    <FieldValue name="hasEditedPaidBreak">
                                        {(
                                            hasEditedPaidBreak:
                                                | boolean
                                                | undefined
                                        ): React.ReactElement => (
                                            <PaidBreakOptions>
                                                {(
                                                    options: any
                                                ): React.ReactElement => (
                                                    <Field
                                                        options={options}
                                                        component={
                                                            RenderDropdown as any
                                                        }
                                                        name="paidBreakMinutes"
                                                        subscription={{
                                                            value: true,
                                                            touched: true,
                                                            error: true
                                                        }}
                                                        label="Total Rest Breaks"
                                                        placeholder="Enter total rest breaks"
                                                        parse={toNull}
                                                        disabled={
                                                            !hasEditedPaidBreak
                                                        }
                                                        clearable={
                                                            hasEditedPaidBreak
                                                        }
                                                        fluid
                                                        search
                                                        selection
                                                        selectOnBlur={false}
                                                    />
                                                )}
                                            </PaidBreakOptions>
                                        )}
                                    </FieldValue>
                                </Grid.Column>
                                <Grid.Column
                                    mobile={16}
                                    tablet={16}
                                    computer={16}
                                >
                                    <Field
                                        name="hasEditedPaidBreak"
                                        component={RenderCheckbox as any}
                                        label="I want to manually enter the paid breaks"
                                    />
                                </Grid.Column>
                            </WorkTypeIsPieceRateGate>
                        </React.Fragment>
                    )}
                </FieldValue>
            </Grid.Row>
            <Grid.Column width={16}>
                <List verticalAlign="middle">
                    <Condition when="totalPaidMinutes">
                        <List.Item>
                            <List.Content floated="right">
                                <Field
                                    name="totalPaidMinutes"
                                    subscription={{ value: true }}
                                    component={RenderValueHours}
                                />
                            </List.Content>
                            <List.Content>
                                <b>Total Hours</b>
                            </List.Content>
                        </List.Item>
                    </Condition>
                    <Condition when="unpaidBreakMinutes">
                        <List.Item>
                            <List.Content floated="right">
                                <Field
                                    name="unpaidBreakMinutes"
                                    subscription={{ value: true }}
                                    component={RenderValue}
                                />
                                {' mins'}
                            </List.Content>
                            <List.Content>
                                <b>Total Meal Breaks</b>
                            </List.Content>
                        </List.Item>
                    </Condition>
                    <FieldValue name="workTypeId">
                        {(
                            workTypeId: number | undefined
                        ): React.ReactElement => (
                            <WorkTypeIsPieceRateGate workTypeId={workTypeId}>
                                <Condition when="earnings">
                                    <List.Item>
                                        <List.Content floated="right">
                                            <Field
                                                name="earnings"
                                                subscription={{ value: true }}
                                                component={RenderValueDollars}
                                            />
                                        </List.Content>
                                        <List.Content>
                                            <b>Earnings</b>
                                        </List.Content>
                                    </List.Item>
                                </Condition>
                                <Condition when="effectiveRate">
                                    <List.Item>
                                        <List.Content floated="right">
                                            <Field
                                                name="effectiveRate"
                                                subscription={{ value: true }}
                                                component={RenderValueDollars}
                                            />
                                        </List.Content>
                                        <List.Content>
                                            <b>Achieved</b>
                                        </List.Content>
                                    </List.Item>
                                </Condition>
                            </WorkTypeIsPieceRateGate>
                        )}
                    </FieldValue>
                </List>
            </Grid.Column>
            <FieldValues fieldNames={['date', 'endTime', 'startTime']}>
                {({ date, endTime, startTime }: any): React.ReactElement => (
                    <MyTimesheetEntryAlreadyExists
                        date={date}
                        endTime={endTime}
                        startTime={startTime}
                    >
                        {(
                            alreadyExists: boolean
                        ): React.ReactElement | null => (
                            <React.Fragment>
                                {alreadyExists && (
                                    <Grid.Column width={16}>
                                        <Message negative>
                                            <Message.Header>
                                                The time range overlaps with an
                                                existing entry.
                                            </Message.Header>
                                        </Message>
                                    </Grid.Column>
                                )}
                                <SubmittingSection
                                    submittingButtonParam={{
                                        disabled: alreadyExists
                                    }}
                                    queryStringCancelButtonParam={{
                                        params: {
                                            [QUERY_STRING_PARAM]: undefined
                                        },
                                        ignoreActive: false
                                    }}
                                />
                            </React.Fragment>
                        )}
                    </MyTimesheetEntryAlreadyExists>
                )}
            </FieldValues>
        </Grid>
    </Form>
);

export default MyTimesheetEntryForm;
